import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  MdDevices,
  MdVerifiedUser,
  MdPending,
  MdCancel,
  MdSearch,
  MdCloudUpload,
  MdFileDownload,
  MdRefresh,
  MdVisibility,
  MdClose,
  MdDownload,
  MdPerson,
  MdLogout,
  MdEdit,
  MdCheckCircle,
} from "react-icons/md";
import ShowSnacker from "./ShowSnacker";
import { apiFetch } from "../../services/api";
import { getCurrentUserRole, hasPermission, normalizeRole } from "../../utils/rbac";
import "../../styles/DashBoard_Devices.css";

const getCurrentLocalDateTimeString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper to canonicalize any MAC ID to XX:XX:XX:XX:XX:XX
export const formatMacWithColon = (raw: any): string => {
  if (!raw) return "";
  const cleaned = String(raw).trim().toUpperCase().replace(/[^0-9A-F]/g, "");
  if (cleaned.length === 12) {
    return cleaned.match(/.{1,2}/g)?.join(":") || cleaned;
  }
  return String(raw).trim().toUpperCase();
};

export const formatDisplayDate = (dStr: any): string => {
  if (!dStr) return "—";
  try {
    const d = new Date(dStr);
    if (isNaN(d.getTime())) return String(dStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  } catch (_) {
    return String(dStr);
  }
};

export const formatDisplayDateTime = (dStr: any): string => {
  if (!dStr) return "—";
  try {
    const d = new Date(dStr);
    if (isNaN(d.getTime())) return String(dStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch (_) {
    return String(dStr);
  }
};

export const getInvoiceUrl = (inv: any, fallbackUrl?: string | null): string | null => {
  if (fallbackUrl) return fallbackUrl;
  if (!inv) return null;
  if (typeof inv === "object" && inv.invoiceDownloadUrl) return inv.invoiceDownloadUrl;
  const raw = typeof inv === "string" ? inv : inv.invoiceDetails;
  if (!raw || typeof raw !== "string" || !raw.trim()) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  const filename = raw.split(/[\\/]/).pop();
  if (!filename) return null;
  const baseUrl = process.env.REACT_APP_API_URL || "https://devicehub.studds.com";
  return `${baseUrl}/device/invoice/${encodeURIComponent(filename)}`;
};

export const isPdfFile = (url: string | null): boolean => {
  if (!url) return false;
  try {
    const decoded = decodeURIComponent(url.split("?")[0].toLowerCase());
    return decoded.endsWith(".pdf") || decoded.includes("application/pdf") || decoded.startsWith("data:application/pdf");
  } catch (_) {
    return url.toLowerCase().includes(".pdf");
  }
};

const initialDeviceForm = {
  // 1. Device Identification & Hardware
  macId: "",
  modelname: "STUDDS LINK",
  firmwareversion: "1.0.0",
  helmetBrand: "STUDDS",
  device_cod: "0x240404",
  status: "Active",

  // 2. Customer, Purchase & Warranty Details
  username: "",
  emailId: "",
  mobilenumber: "",
  placeofPurchase: "",
  dateofPurchase: "",
  dealerName: "",
  warrantyApproved: "Pending",
  city: "",
  state: "",
  country: "India",
  zipcode: "",
  location: "",
  OS: "Android",
  invoiceDetails: "",

  // 3. Quality Control (QC) & Hardware Testing
  overall_qc_status: "PASS",
  inspector_name: "",
  inspection_date: getCurrentLocalDateTimeString(),
  pairing_status: "PASS",
  pairing_time_s: "0.78",
  reconnect_time_s: "2.21",
  pairing_remarks: "",
  battery_level: "100",
  profiles_found: "SPP, A2DP, AVRCP, HFP/HSP",
  physical_shell: "PASS",
  physical_remarks: "",
  button_tactility: "PASS",
  button_remarks: "",
  microphone_quality: "PASS",
  mic_remarks: "",
  speaker_output: "PASS",
  speaker_remarks: "",
  attempt_number: "1",
  duplicate_status: "Original",
  qc_history: "",
};

function DashBoardDevices() {
  const navigate = useNavigate();
  const [snack, setSnack] = useState({ open: false, message: "", type: "info" });

  const [userRole, setUserRole] = useState(getCurrentUserRole());
  const canEditDevice = hasPermission("canEditDevice", userRole);
  const canUploadDeviceExcel = hasPermission("canUploadDeviceExcel", userRole);

  const getLoggedInUser = useCallback((): string => {
    const storedName = localStorage.getItem("userName");
    if (storedName && storedName.trim()) return storedName.trim();
    const storedEmail = localStorage.getItem("userEmail");
    if (storedEmail && storedEmail.trim()) {
      return storedEmail.split("@")[0];
    }
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const parts = token.split(".");
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          if (payload.name) return payload.name;
          if (payload.email) return payload.email.split("@")[0];
        }
      } catch (e) {}
    }
    return "Inspector";
  }, []);

  useEffect(() => {
    const handleRoleChange = (e: any) => {
      const fresh = e?.detail?.role ? normalizeRole(e.detail.role) : getCurrentUserRole();
      setUserRole(fresh);
    };
    window.addEventListener("roleChanged", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);
    return () => {
      window.removeEventListener("roleChanged", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, []);

  const [AllDevices, setAllDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Filter states
  const [search, setSearch] = useState("");
  const [model, setModel] = useState("All");
  const [warrantyStatus, setWarrantyStatus] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [selectDate, setselectedDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal & form states
  const [showForm, setshowForm] = useState(false);
  const [Mode, setMode] = useState(""); // "single" | "edit" | "multi" | "details"
  const [selectedDevice, setselectedDevice] = useState<any>(null);
  const [deviceMessages, setDeviceMessages] = useState<string[]>([]);

  // Comprehensive Form State for Add / Edit
  const [deviceForm, setDeviceForm] = useState(initialDeviceForm);
  const [isSubmittingDevice, setIsSubmittingDevice] = useState(false);

  // Excel upload file
  const [xlFile, setxlFile] = useState<File | null>(null);
  const FileUseRef = useRef<HTMLInputElement | null>(null);

  // Invoice file upload for Edit Warranty Registration
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const invoiceFileInputRef = useRef<HTMLInputElement | null>(null);

  // Invoice Document Viewer Modal Popup
  const [invoiceModalUrl, setInvoiceModalUrl] = useState<string | null>(null);
  const [invoiceModalTitle, setInvoiceModalTitle] = useState<string>("Invoice Document");

  const openInvoiceModal = (url: string | null, title?: string) => {
    if (!url) return;
    setInvoiceModalUrl(url);
    if (title) setInvoiceModalTitle(title);
  };

  const allowedExtensions = ["xls", "xlsx"];
  const isValidExtension = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    return ext ? allowedExtensions.includes(ext) : false;
  };

  const ShowSnackbar = (message: string, type = "info") => {
    setSnack({ open: true, message, type });
  };

  // Fetch all devices from database
  const fetchalldevices = async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/device/all-devices", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      const result = await res.json();
      setAllDevices(result.Alldevices || []);
    } catch (err) {
      setAllDevices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchalldevices();

    const handleFocus = () => {
      fetchalldevices();
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const refreshPage = () => {
    fetchalldevices();
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("auth");
    localStorage.clear();
    navigate("/login", { replace: true });
  };

  const clearFilters = () => {
    setSearch("");
    setModel("All");
    setWarrantyStatus("");
    setCitySearch("");
    setselectedDate("");
    setCurrentPage(1);
  };

  // Determine warranty status based on purchase date and DB status
  const getWarrantyStatus = (device: any): "approved" | "expired" | "pending" => {
    if (!device) return "pending";

    // Warranty Activation Restriction:
    // If the overall QC status is Fail, the device warranty must NOT be activated -> it should be Pending status
    const isQcFail =
      String(device.overall_qc_status || "").trim().toUpperCase() === "FAIL" ||
      String(device.overall_status || "").trim().toUpperCase() === "FAIL" ||
      String(device.overall_status || "").trim().toUpperCase() === "QC FAILED" ||
      String(device.overall_status || "").trim().toUpperCase().includes("FAIL") ||
      String(device.pairing_status || "").trim().toUpperCase() === "FAIL";

    if (isQcFail) {
      return "pending";
    }

    if (device.dateofPurchase) {
      const purchaseDate = new Date(device.dateofPurchase);
      if (!isNaN(purchaseDate.getTime())) {
        const expiryDate = new Date(purchaseDate);
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        expiryDate.setHours(0, 0, 0, 0);

        if (today >= expiryDate) {
          return "expired";
        }
      }
    }

    const dbStatus = String(device.warrantyApproved || "").trim().toLowerCase();
    if (dbStatus === "approved" || dbStatus === "active") return "approved";
    if (dbStatus === "expired") return "expired";
    return "pending";
  };

  // Calculate unique models for dropdown
  const uniqueModels = useMemo(() => {
    return Array.from(
      new Set(
        AllDevices.map((d) => d.modelname).filter(
          (m) => m && typeof m === "string" && m.trim() !== ""
        )
      )
    );
  }, [AllDevices]);

  // Statistics
  const stats = useMemo(() => {
    const total = AllDevices.length;
    let approvedCount = 0;
    let expiredCount = 0;
    let pendingCount = 0;

    AllDevices.forEach((device) => {
      const status = getWarrantyStatus(device);
      if (status === "approved") approvedCount++;
      else if (status === "expired") expiredCount++;
      else pendingCount++;
    });

    return { total, approvedCount, expiredCount, pendingCount };
  }, [AllDevices]);

  // Filter devices
  const filteredDevices = useMemo(() => {
    return AllDevices.filter((device) => {
      const q = search.toLowerCase().trim();
      const matchesSearch =
        !q ||
        (device.modelname && device.modelname.toLowerCase().includes(q)) ||
        (device.macId && device.macId.toLowerCase().includes(q)) ||
        (device.OS && device.OS.toLowerCase().includes(q)) ||
        (device.emailId && device.emailId.toLowerCase().includes(q)) ||
        (device.mobilenumber && device.mobilenumber.toLowerCase().includes(q));

      const matchesModel = model === "All" || device.modelname === model;

      const actualWarrantyStatus = getWarrantyStatus(device);
      const matchesWarranty =
        !warrantyStatus || actualWarrantyStatus === warrantyStatus;

      const matchesCity =
        !citySearch ||
        (device.placeofPurchase &&
          device.placeofPurchase.toLowerCase().includes(citySearch.toLowerCase())) ||
        (device.city && device.city.toLowerCase().includes(citySearch.toLowerCase()));

      const formattedPurchaseDate = device.dateofPurchase
        ? new Date(device.dateofPurchase).toLocaleDateString("en-CA")
        : "";
      const formattedRegDate = device.createdAt
        ? new Date(device.createdAt).toLocaleDateString("en-CA")
        : "";
      const matchesDate =
        !selectDate ||
        formattedPurchaseDate === selectDate ||
        formattedRegDate === selectDate;

      return matchesSearch && matchesModel && matchesWarranty && matchesCity && matchesDate;
    });
  }, [AllDevices, search, model, warrantyStatus, citySearch, selectDate]);

  // Pagination
  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage) || 1;
  const paginatedDevices = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredDevices.slice(start, start + itemsPerPage);
  }, [filteredDevices, currentPage, itemsPerPage]);

  // Table Columns Definition (Comprehensive Fleet & Quality Control Checkpoints)
  // Table Columns Definition for Warranty Registration Export
  const columns = [
    { header: "SR.NO.", field: "srno" },
    { header: "Device MAC ID", field: "macId" },
    { header: "Model Name", field: "modelname" },
    { header: "Firmware Version", field: "firmwareversion" },
    { header: "Operational Status", field: "status" },
    { header: "Customer Name", field: "username" },
    { header: "Email ID", field: "emailId" },
    { header: "Mobile", field: "mobilenumber" },
    { header: "OS", field: "OS" },
    { header: "Date of Purchase", field: "dateofPurchase" },
    { header: "Warranty Registration Date", field: "createdAt" },
    { header: "Place of Purchase", field: "placeofPurchase" },
    { header: "Dealer Name", field: "dealerName" },
    { header: "City", field: "city" },
    { header: "State", field: "state" },
    { header: "Country", field: "country" },
    { header: "Zipcode", field: "zipcode" },
    { header: "Warranty Status", field: "warrantyApproved" },
  ];

  // Excel Export: Warranty Registration Data
  const handleDownload = () => {
    const dataToExport = filteredDevices;
    if (!dataToExport || dataToExport.length === 0) {
      ShowSnackbar("No devices available to export", "info");
      return;
    }

    const formattedData = dataToExport.map((device, idx) => {
      const row: Record<string, any> = {};
      columns.forEach((col) => {
        if (col.field === "srno") {
          row[col.header.toUpperCase()] = idx + 1;
        } else if (col.field === "warrantyApproved") {
          row[col.header.toUpperCase()] = getWarrantyStatus(device).toUpperCase();
        } else if (col.field === "createdAt") {
          row[col.header.toUpperCase()] = formatDisplayDate(device.createdAt || device.registration_date || device.dateofPurchase);
        } else if (col.field === "dateofPurchase") {
          row[col.header.toUpperCase()] = device.dateofPurchase
            ? new Date(device.dateofPurchase).toLocaleDateString("en-GB").replace(/\//g, "-")
            : "";
        } else {
          row[col.header.toUpperCase()] = (device as Record<string, any>)[col.field] ?? "";
        }
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Warranty Registration");
    XLSX.writeFile(workbook, `Warranty_Registration_${new Date().toISOString().split("T")[0]}.xlsx`);
    ShowSnackbar(`Exported ${formattedData.length} warranty registration records to Excel!`, "success");
  };

  // Download sample template
  const downloadSampleExcel = () => {
    const sampleData = [
      {
        macId: "STUDDS001",
        modelname: "Studds Delta",
        firmwareversion: "1.0.0",
        warrantyApproved: "Approved",
      },
      {
        macId: "STUDDS002",
        modelname: "Studds Alpha",
        firmwareversion: "2.0.0",
        warrantyApproved: "Expired",
      },
      {
        macId: "STUDDS003",
        modelname: "Studds Sensor",
        firmwareversion: "3.0.0",
        warrantyApproved: "Pending",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Devices");
    XLSX.writeFile(workbook, "sample_devices.xlsx");
  };

  // Form Resets
  const resetForm1 = () => {
    if (FileUseRef.current) {
      FileUseRef.current.value = "";
    }
    setxlFile(null);
  };

  // Open Edit Device Modal with all fields pre-filled from selected device
  const editdevice = (device: any) => {
    setInvoiceFile(null);
    if (invoiceFileInputRef.current) {
      invoiceFileInputRef.current.value = "";
    }

    let formattedDate = "";
    if (device.dateofPurchase) {
      try {
        const d = new Date(device.dateofPurchase);
        if (!isNaN(d.getTime())) {
          formattedDate = d.toISOString().split("T")[0];
        } else {
          formattedDate = String(device.dateofPurchase);
        }
      } catch (e) {
        formattedDate = String(device.dateofPurchase);
      }
    }

    let formattedInspDate = "";
    if (device.inspection_date) {
      try {
        const d = new Date(device.inspection_date);
        if (!isNaN(d.getTime())) {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          const hours = String(d.getHours()).padStart(2, "0");
          const minutes = String(d.getMinutes()).padStart(2, "0");
          formattedInspDate = `${year}-${month}-${day}T${hours}:${minutes}`;
        } else {
          formattedInspDate = getCurrentLocalDateTimeString();
        }
      } catch (e) {
        formattedInspDate = getCurrentLocalDateTimeString();
      }
    } else {
      formattedInspDate = getCurrentLocalDateTimeString();
    }

    const isDeviceQcFail =
      String(device.overall_qc_status || "").trim().toUpperCase() === "FAIL" ||
      String(device.overall_status || "").trim().toUpperCase() === "FAIL" ||
      String(device.overall_status || "").trim().toUpperCase() === "QC FAILED" ||
      String(device.overall_status || "").trim().toUpperCase().includes("FAIL") ||
      String(device.pairing_status || "").trim().toUpperCase() === "FAIL";

    setDeviceForm({
      macId: device.macId || "",
      modelname: device.modelname || device.modelName || "STUDDS LINK",
      firmwareversion: device.firmwareversion || device.firmware_version || "1.0.0",
      helmetBrand: device.helmetBrand || "STUDDS",
      device_cod: device.device_cod || "0x240404",
      status: device.status || "Active",

      username: device.username || "",
      emailId: device.emailId || "",
      mobilenumber: device.mobilenumber || "",
      placeofPurchase: device.placeofPurchase || "",
      dateofPurchase: formattedDate,
      dealerName: device.dealerName || "",
      warrantyApproved: isDeviceQcFail ? "Pending" : (device.warrantyApproved || "Pending"),
      city: device.city || "",
      state: device.state || "",
      country: device.country || "India",
      zipcode: device.zipcode || "",
      location: typeof device.location === "object" ? JSON.stringify(device.location) : (device.location || ""),
      OS: device.OS || "Android",
      invoiceDetails: device.invoiceDetails || "",

      overall_qc_status: device.overall_qc_status || device.overall_status || (device.pairing_status === "PASS" ? "PASS" : device.pairing_status === "FAIL" ? "FAIL" : "PASS"),
      inspector_name: device.inspector_name || getLoggedInUser(),
      inspection_date: formattedInspDate || getCurrentLocalDateTimeString(),
      pairing_status: device.pairing_status || "PASS",
      pairing_time_s: device.pairing_time_s !== null && device.pairing_time_s !== undefined ? String(device.pairing_time_s) : "0.78",
      reconnect_time_s: device.reconnect_time_s !== null && device.reconnect_time_s !== undefined ? String(device.reconnect_time_s) : "2.21",
      pairing_remarks: device.pairing_remarks || "",
      battery_level: device.battery_level !== null && device.battery_level !== undefined ? String(device.battery_level) : "100",
      profiles_found: device.profiles_found || "SPP, A2DP, AVRCP, HFP/HSP",
      physical_shell: device.physical_shell || "PASS",
      physical_remarks: device.physical_remarks || "",
      button_tactility: device.button_tactility || "PASS",
      button_remarks: device.button_remarks || "",
      microphone_quality: device.microphone_quality || "PASS",
      mic_remarks: device.mic_remarks || "",
      speaker_output: device.speaker_output || "PASS",
      speaker_remarks: device.speaker_remarks || "",
      attempt_number: device.attempt_number !== null && device.attempt_number !== undefined ? String(device.attempt_number) : "1",
      duplicate_status: device.duplicate_status || "Original",
      qc_history: typeof device.qc_history === "object" ? JSON.stringify(device.qc_history) : (device.qc_history || ""),
    });
    setMode("edit");
    setshowForm(true);
  };

  // Open View Device Details Modal
  const viewdevice = (device: any) => {
    setselectedDevice(device);
    let msgs: string[] = [];
    if (device.messages && Array.isArray(device.messages)) {
      msgs = device.messages;
    } else if (device.qc_history) {
      try {
        const parsed = typeof device.qc_history === "string" ? JSON.parse(device.qc_history) : device.qc_history;
        if (Array.isArray(parsed)) {
          msgs = parsed.map((p) => (typeof p === "string" ? p : JSON.stringify(p)));
        } else if (parsed && typeof parsed === "object") {
          msgs = Object.entries(parsed).map(([k, v]) => `${k}: ${v}`);
        } else if (parsed) {
          msgs = [String(parsed)];
        }
      } catch (_) {
        msgs = [String(device.qc_history)];
      }
    } else if (device.pairing_remarks || device.physical_remarks || device.button_remarks || device.mic_remarks || device.speaker_remarks) {
      msgs = [
        device.pairing_remarks ? `Pairing: ${device.pairing_remarks}` : "",
        device.physical_remarks ? `Physical: ${device.physical_remarks}` : "",
        device.button_remarks ? `Button: ${device.button_remarks}` : "",
        device.mic_remarks ? `Mic: ${device.mic_remarks}` : "",
        device.speaker_remarks ? `Speaker: ${device.speaker_remarks}` : "",
      ].filter(Boolean);
    }
    setDeviceMessages(msgs);
    setMode("details");
    setshowForm(true);
  };

  // Handle Edit Device
  const handleEditDeviceSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!deviceForm.macId || !deviceForm.macId.trim()) {
      ShowSnackbar("MAC ID is required", "error");
      return;
    }

    const isQcFail =
      String(deviceForm.overall_qc_status || "").toUpperCase() === "FAIL" ||
      String(deviceForm.overall_status || "").toUpperCase() === "FAIL" ||
      String(deviceForm.overall_status || "").toUpperCase() === "QC FAILED" ||
      String(deviceForm.pairing_status || "").toUpperCase() === "FAIL";

    const payloadForm = { ...deviceForm };
    if (isQcFail && String(payloadForm.warrantyApproved).toLowerCase() === "approved") {
      payloadForm.warrantyApproved = "Pending";
      setDeviceForm((prev) => ({ ...prev, warrantyApproved: "Pending" }));
      ShowSnackbar("Overall QC status is Fail: Device warranty cannot be activated and has been set to Pending.", "warning");
    }

    setIsSubmittingDevice(true);
    try {
      let res: Response;
      if (invoiceFile) {
        const formData = new FormData();
        formData.append("invoice", invoiceFile);
        formData.append("data", JSON.stringify(payloadForm));
        res = await apiFetch(`/device/update-device/${encodeURIComponent(payloadForm.macId)}`, {
          method: "PUT",
          body: formData,
        });
      } else {
        res = await apiFetch(`/device/update-device/${encodeURIComponent(payloadForm.macId)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadForm),
        });
      }

      const data = await res.json();
      if (res.ok) {
        ShowSnackbar(`Warranty registration for '${deviceForm.macId.toUpperCase()}' updated successfully!`, "success");
        if (selectedDevice && selectedDevice.macId === deviceForm.macId) {
          setselectedDevice({
            ...selectedDevice,
            ...deviceForm,
            invoiceDetails: data.invoiceDetails || deviceForm.invoiceDetails,
            invoiceDownloadUrl: data.invoiceDownloadUrl || selectedDevice.invoiceDownloadUrl,
          });
        }
        setInvoiceFile(null);
        if (invoiceFileInputRef.current) {
          invoiceFileInputRef.current.value = "";
        }
        setshowForm(false);
        setMode("");
        fetchalldevices();
      } else {
        ShowSnackbar(data.message || "Failed to update warranty registration", "error");
      }
    } catch (err: any) {
      ShowSnackbar(err.message || "Server error updating warranty registration", "error");
    } finally {
      setIsSubmittingDevice(false);
    }
  };

  // Upload Excel Devices
  const handleUpload = async () => {
    if (!xlFile) {
      ShowSnackbar("Please select an Excel file to upload", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event: any) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (!jsonData.length) {
          ShowSnackbar("Excel sheet is empty", "error");
          return;
        }

        const requiredFields = ["macId", "modelname", "firmwareversion", "warrantyApproved"];
        const invalidRow = jsonData.find((row: any) =>
          requiredFields.some((field) => !row[field])
        );

        if (invalidRow) {
          ShowSnackbar(
            "Excel format invalid. Required columns: macId, modelname, firmwareversion, warrantyApproved",
            "error"
          );
          return;
        }

        const response = await apiFetch("/device/upload-devices", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jsonData }),
        });

        const res = await response.json();
        if (response.ok) {
          ShowSnackbar(
            `${res.insertedCount || 0} device(s) added successfully. ${
              res.duplicateCount || 0
            } duplicate(s) skipped.`,
            "success"
          );
          setshowForm(false);
          setMode("");
          resetForm1();
          fetchalldevices();
        } else if (response.status === 409) {
          ShowSnackbar(`${res.duplicateCount} duplicate device(s) skipped.`, "error");
          setshowForm(false);
          setMode("");
          resetForm1();
          fetchalldevices();
        } else {
          ShowSnackbar(res.message || "Failed to upload devices", "error");
          setshowForm(false);
          setMode("");
          resetForm1();
        }
      } catch (err) {
        ShowSnackbar("Error reading Excel file", "error");
      }
    };

    reader.readAsArrayBuffer(xlFile);
  };

  return (
    <div className="dashboard-devices user-list-container">
      {/* Top Header */}
      <div className="devices-header-row">
        <div className="header-title-section">
          <h2>Warranty Registration</h2>
          <p>Customer device warranty registration, purchase details, and verification status</p>
        </div>
        <div className="header-actions-row">
          {canUploadDeviceExcel && (
            <button
              type="button"
              className="devices-secondary-btn"
              onClick={() => {
                setshowForm(true);
                setMode("multi");
              }}
            >
              <MdCloudUpload size={18} />
              <span>Upload Excel</span>
            </button>
          )}
          <div className="header-round-icons">
            <span
              className="round-icon-btn"
              onClick={refreshPage}
              title="Refresh"
            >
              <MdRefresh size={19} color="#4b5563" />
            </span>
            <span
              className="round-icon-btn"
              onClick={() => navigate("/dashboard/profile")}
              title="Profile"
            >
              <MdPerson size={19} color="#374151" />
            </span>
            <span
              className="round-icon-btn logout"
              onClick={handleLogout}
              title="Logout"
            >
              <MdLogout size={19} color="#dc2626" />
            </span>
          </div>
        </div>
      </div>

      {/* Summary Stat Cards */}
      <div className="user-stats-container">
        <div className="user-stat-card">
          <div className="stat-icon total">
            <MdDevices />
          </div>
          <div className="stat-info">
            <span className="stat-label">Total Devices</span>
            <span className="stat-value">{stats.total}</span>
          </div>
        </div>
        <div className="user-stat-card">
          <div className="stat-icon approved">
            <MdVerifiedUser />
          </div>
          <div className="stat-info">
            <span className="stat-label">Approved Warranties</span>
            <span className="stat-value">{stats.approvedCount}</span>
          </div>
        </div>
        <div className="user-stat-card">
          <div className="stat-icon pending">
            <MdPending />
          </div>
          <div className="stat-info">
            <span className="stat-label">Pending Warranties</span>
            <span className="stat-value">{stats.pendingCount}</span>
          </div>
        </div>
        <div className="user-stat-card">
          <div className="stat-icon expired">
            <MdCancel />
          </div>
          <div className="stat-info">
            <span className="stat-label">Expired Warranties</span>
            <span className="stat-value">{stats.expiredCount}</span>
          </div>
        </div>
      </div>

      {/* Filters & Search Bar */}
      <div className="user-filter-bar">
        <div className="filter-left">
          <div className="filter-search-box">
            <MdSearch className="search-icon-inside" />
            <input
              type="text"
              placeholder="Search by MAC ID, Model, OS, Email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <select
            className="filter-select"
            value={model}
            onChange={(e) => {
              setModel(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Models</option>
            {uniqueModels.map((m, index) => (
              <option key={index} value={m}>
                {m}
              </option>
            ))}
          </select>

          <select
            className="filter-select"
            value={warrantyStatus}
            onChange={(e) => {
              setWarrantyStatus(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="expired">Expired</option>
          </select>

          <div className="filter-search-box city-filter-box">
            <input
              type="text"
              placeholder="Filter by city..."
              value={citySearch}
              onChange={(e) => {
                setCitySearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <input
            type="date"
            className="filter-select date-filter-input"
            value={selectDate}
            onChange={(e) => {
              setselectedDate(e.target.value);
              setCurrentPage(1);
            }}
            title="Filter by Purchase or Registration Date"
          />

          {(search || model !== "All" || warrantyStatus || citySearch || selectDate) && (
            <button className="btn-clear-filter" onClick={clearFilters}>
              Clear Filter
            </button>
          )}
        </div>

        <div className="filter-right">
          <span className="filter-count">
            Showing <strong>{filteredDevices.length}</strong> of{" "}
            <strong>{AllDevices.length}</strong> devices
          </span>
          <button
            className="btn-export-excel"
            onClick={handleDownload}
            title="Export filtered warranty registrations to Excel"
          >
            <MdFileDownload size={16} /> Export Excel
          </button>
          <button className="btn-refresh" onClick={refreshPage}>
            <MdRefresh size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* Devices Table */}
      <div className="user-table-wrapper" style={{ width: "100%", overflowX: "auto" }}>
        <table className="user-table" style={{ width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: "65px", textAlign: "center" }}>SL NO.</th>
              <th>MAC ID</th>
              <th>MODEL NAME</th>
              <th>FIRMWARE</th>
              <th style={{ textAlign: "center" }}>WARRANTY REGISTRATION DATE</th>
              <th style={{ textAlign: "center" }}>WARRANTY STATUS</th>
              <th style={{ textAlign: "center", width: "160px" }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: "40px" }}>
                  <p style={{ color: "#666", fontSize: "15px" }}>Loading devices...</p>
                </td>
              </tr>
            ) : paginatedDevices.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="table-empty-state">
                    <MdDevices />
                    <h4>No devices found</h4>
                    <p>Try adjusting your search or filter criteria.</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedDevices.map((device, idx) => {
                const slNo = (currentPage - 1) * itemsPerPage + idx + 1;
                const status = getWarrantyStatus(device);
                const regDate = formatDisplayDate(device.createdAt || device.registration_date || device.dateofPurchase);

                return (
                  <tr key={device.macId || idx}>
                    <td style={{ textAlign: "center", fontWeight: "600", color: "#6b7280" }}>
                      {slNo}
                    </td>
                    <td>
                      <span className="device-mac-badge">{device.macId || "—"}</span>
                    </td>
                    <td>
                      <strong style={{ color: "#111827" }}>{device.modelname || "—"}</strong>
                    </td>
                    <td>{device.firmwareversion || "—"}</td>
                    <td style={{ textAlign: "center", color: "#374151", fontWeight: 500, whiteSpace: "nowrap" }}>
                      {regDate}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {status === "approved" ? (
                        <span className="warranty-pill approved">
                          <MdVerifiedUser size={14} /> Active
                        </span>
                      ) : status === "expired" ? (
                        <span className="warranty-pill expired">
                          <MdCancel size={14} /> Expired
                        </span>
                      ) : (
                        <span className="warranty-pill pending">
                          <MdPending size={14} /> Pending
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <div className="action-buttons-group" style={{ justifyContent: "center" }}>
                        {canEditDevice && (
                          <button
                            type="button"
                            className="action-btn edit"
                            onClick={() => editdevice(device)}
                            title="Edit warranty registration entry"
                          >
                            <MdEdit size={14} />
                            EDIT
                          </button>
                        )}
                        <button
                          type="button"
                          className="action-btn view"
                          onClick={() => viewdevice(device)}
                          title="View warranty registration details"
                        >
                          <MdVisibility size={14} />
                          VIEW
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination Footer */}
        {filteredDevices.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing{" "}
              <strong>
                {filteredDevices.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
              </strong>{" "}
              to{" "}
              <strong>
                {Math.min(currentPage * itemsPerPage, filteredDevices.length)}
              </strong>{" "}
              of <strong>{filteredDevices.length}</strong> devices
            </div>
            <div className="pagination-controls">
              <select
                className="pagination-select"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5 / page</option>
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
              <button
                className="pagination-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              >
                Previous
              </button>
              <span className="pagination-page-indicator">
                {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Edit Device */}
      {showForm && Mode === "edit" && canEditDevice && (
        <div
          className="user-modal-overlay"
          onClick={() => {
            setMode("");
            setshowForm(false);
          }}
        >
          <div
            className="user-modal-card device-modal-card"
            style={{ maxWidth: "1080px", width: "95vw", maxHeight: "92vh", display: "flex", flexDirection: "column" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-modal-header">
              <div className="modal-title-wrap">
                <MdEdit className="modal-header-icon" />
                <div>
                  <h3 style={{ margin: 0 }}>
                    Edit Warranty Registration ({deviceForm.macId || "Unknown"})
                  </h3>
                  <span style={{ fontSize: "12px", color: "#ffffff", opacity: 0.95 }}>
                    Update device specifications, registered customer details, and warranty information
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="user-modal-close-btn"
                onClick={() => {
                  setMode("");
                  setshowForm(false);
                }}
                title="Close"
              >
                <MdClose />
              </button>
            </div>

            <div className="user-modal-body" style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
              <form onSubmit={handleEditDeviceSubmit}>
                {/* SECTION 1: Device Identification & Specifications */}
                <div style={{ marginBottom: "24px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", paddingBottom: "6px", borderBottom: "2px solid #fee2e2" }}>
                    <MdDevices style={{ color: "#dc2626", fontSize: "18px" }} />
                    <span style={{ margin: 0, color: "#111827", fontSize: "14px", fontWeight: 700, background: "transparent" }}>
                      Device Specifications
                    </span>
                  </div>
                  <div className="user-form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
                    {/* MAC ID */}
                    <div className="user-form-group">
                      <label>
                        Device MAC ID <span style={{ color: "#e60000" }}>*</span>
                      </label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. 04:EC:A9:10:00:04"
                          value={deviceForm.macId}
                          disabled={true}
                          style={{ background: "#f1f5f9", cursor: "not-allowed", fontFamily: "monospace", fontWeight: 700, padding: "0 14px" }}
                          title="MAC address is permanent and cannot be modified"
                        />
                      </div>
                    </div>

                    {/* Model Name */}
                    <div className="user-form-group">
                      <label>
                        Model Name <span style={{ color: "#e60000" }}>*</span>
                      </label>
                      <div className="user-input-box">
                        <select
                          value={deviceForm.modelname}
                          onChange={(e) => setDeviceForm({ ...deviceForm, modelname: e.target.value })}
                          style={{ padding: "0 14px" }}
                          required
                        >
                          <option value="STUDDS RYDIO">STUDDS RYDIO</option>
                          <option value="STUDDS LINK">STUDDS LINK</option>
                          <option value="SMK KONNECT">SMK KONNECT</option>
                        </select>
                      </div>
                    </div>

                    {/* Firmware Version */}
                    <div className="user-form-group">
                      <label>Firmware Version</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. 1.0.0"
                          value={deviceForm.firmwareversion}
                          onChange={(e) => setDeviceForm({ ...deviceForm, firmwareversion: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Helmet Brand */}
                    <div className="user-form-group">
                      <label>Helmet Brand</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. STUDDS"
                          value={deviceForm.helmetBrand}
                          onChange={(e) => setDeviceForm({ ...deviceForm, helmetBrand: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Device COD */}
                    <div className="user-form-group">
                      <label>Device COD</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. 0x240404"
                          value={deviceForm.device_cod}
                          onChange={(e) => setDeviceForm({ ...deviceForm, device_cod: e.target.value })}
                          style={{ padding: "0 14px", fontFamily: "monospace" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 2: Customer Registration Details */}
                <div style={{ marginBottom: "24px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", paddingBottom: "6px", borderBottom: "2px solid #fee2e2" }}>
                    <MdPerson style={{ color: "#dc2626", fontSize: "18px" }} />
                    <span style={{ margin: 0, color: "#111827", fontSize: "14px", fontWeight: 700, background: "transparent" }}>
                      Customer Registration Details
                    </span>
                  </div>
                  <div className="user-form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
                    {/* Customer Name */}
                    <div className="user-form-group">
                      <label>Registered Customer Name</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. Rishabh Gupta"
                          value={deviceForm.username}
                          onChange={(e) => setDeviceForm({ ...deviceForm, username: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Customer Email ID */}
                    <div className="user-form-group">
                      <label>Customer Email ID</label>
                      <div className="user-input-box">
                        <input
                          type="email"
                          placeholder="e.g. customer@example.com"
                          value={deviceForm.emailId}
                          onChange={(e) => setDeviceForm({ ...deviceForm, emailId: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Mobile Number */}
                    <div className="user-form-group">
                      <label>Mobile Number</label>
                      <div className="user-input-box">
                        <input
                          type="tel"
                          placeholder="e.g. 9876543210"
                          value={deviceForm.mobilenumber}
                          onChange={(e) => setDeviceForm({ ...deviceForm, mobilenumber: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Operating System */}
                    <div className="user-form-group">
                      <label>Operating System (OS)</label>
                      <div className="user-input-box">
                        <select
                          value={deviceForm.OS}
                          onChange={(e) => setDeviceForm({ ...deviceForm, OS: e.target.value })}
                          style={{ padding: "0 14px" }}
                        >
                          <option value="Android">Android</option>
                          <option value="iOS">iOS</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 3: Purchase & Dealer Information */}
                <div style={{ marginBottom: "24px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", paddingBottom: "6px", borderBottom: "2px solid #fee2e2" }}>
                    <MdDevices style={{ color: "#dc2626", fontSize: "18px" }} />
                    <span style={{ margin: 0, color: "#111827", fontSize: "14px", fontWeight: 700, background: "transparent" }}>
                      Purchase & Dealer Information
                    </span>
                  </div>
                  <div className="user-form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
                    {/* Date of Purchase */}
                    <div className="user-form-group">
                      <label>Date of Purchase</label>
                      <div className="user-input-box">
                        <input
                          type="date"
                          value={deviceForm.dateofPurchase}
                          onChange={(e) => setDeviceForm({ ...deviceForm, dateofPurchase: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Place of Purchase */}
                    <div className="user-form-group">
                      <label>Place of Purchase</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. Delhi Store, Online"
                          value={deviceForm.placeofPurchase}
                          onChange={(e) => setDeviceForm({ ...deviceForm, placeofPurchase: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Dealer Name */}
                    <div className="user-form-group">
                      <label>Dealer Name</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. Studds Authorized Dealer"
                          value={deviceForm.dealerName}
                          onChange={(e) => setDeviceForm({ ...deviceForm, dealerName: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* City */}
                    <div className="user-form-group">
                      <label>City</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. New Delhi"
                          value={deviceForm.city}
                          onChange={(e) => setDeviceForm({ ...deviceForm, city: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* State */}
                    <div className="user-form-group">
                      <label>State</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. Delhi"
                          value={deviceForm.state}
                          onChange={(e) => setDeviceForm({ ...deviceForm, state: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Country */}
                    <div className="user-form-group">
                      <label>Country</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. India"
                          value={deviceForm.country}
                          onChange={(e) => setDeviceForm({ ...deviceForm, country: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>

                    {/* Postal / Zipcode */}
                    <div className="user-form-group">
                      <label>Postal / Zipcode</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. 110001"
                          value={deviceForm.zipcode}
                          onChange={(e) => setDeviceForm({ ...deviceForm, zipcode: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 4: Warranty Status & Invoice */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", paddingBottom: "6px", borderBottom: "2px solid #fee2e2" }}>
                    <MdVerifiedUser style={{ color: "#dc2626", fontSize: "18px" }} />
                    <span style={{ margin: 0, color: "#111827", fontSize: "14px", fontWeight: 700, background: "transparent" }}>
                      Warranty Status & Invoice
                    </span>
                  </div>
                  <div className="user-form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
                    {/* Warranty Status */}
                    <div className="user-form-group">
                      {(() => {
                        const isQcFail =
                          String(deviceForm.overall_qc_status || "").toUpperCase() === "FAIL" ||
                          String(deviceForm.overall_status || "").toUpperCase() === "FAIL" ||
                          String(deviceForm.overall_status || "").toUpperCase() === "QC FAILED" ||
                          String(deviceForm.pairing_status || "").toUpperCase() === "FAIL";
                        return (
                          <>
                            <label>
                              Warranty Approval Status
                              {isQcFail && (
                                <span style={{ color: "#dc2626", fontSize: "11px", marginLeft: "6px", fontWeight: 700 }}>
                                  (QC Failed — Cannot be Activated)
                                </span>
                              )}
                            </label>
                            <div className="user-input-box">
                              <select
                                value={isQcFail ? "Pending" : deviceForm.warrantyApproved}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (isQcFail && val === "Approved") {
                                    ShowSnackbar("Overall QC status is Fail: Device warranty cannot be activated and must remain Pending.", "warning");
                                    setDeviceForm({ ...deviceForm, warrantyApproved: "Pending" });
                                    return;
                                  }
                                  setDeviceForm({ ...deviceForm, warrantyApproved: val });
                                }}
                                style={{ padding: "0 14px" }}
                              >
                                <option value="Approved" disabled={isQcFail}>
                                  Approved {isQcFail ? "(Disabled - QC Failed)" : ""}
                                </option>
                                <option value="Pending">Pending</option>
                                <option value="Expired">Expired</option>
                                <option value="Denied">Denied</option>
                              </select>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* Invoice Number / Details */}
                    <div className="user-form-group">
                      <label>Invoice Number / Reference</label>
                      <div className="user-input-box">
                        <input
                          type="text"
                          placeholder="e.g. INV-2024-0012 or reference"
                          value={deviceForm.invoiceDetails}
                          onChange={(e) => setDeviceForm({ ...deviceForm, invoiceDetails: e.target.value })}
                          style={{ padding: "0 14px" }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Invoice Document Management: View Current + Upload New */}
                  <div style={{ marginTop: "16px", padding: "14px 16px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "8px" }}>
                      Invoice Document File
                    </label>

                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={invoiceFileInputRef}
                      style={{ display: "none" }}
                      accept=".pdf,image/png,image/jpeg,image/jpg,image/webp"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const validExts = ["pdf", "png", "jpg", "jpeg", "webp"];
                        const ext = file.name.split(".").pop()?.toLowerCase();
                        if (!ext || !validExts.includes(ext)) {
                          ShowSnackbar("Only PDF and image files (PNG, JPG) are allowed", "error");
                          e.target.value = "";
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          ShowSnackbar("File size must not exceed 5 MB", "error");
                          e.target.value = "";
                          return;
                        }
                        setInvoiceFile(file);
                      }}
                    />

                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}>
                      {/* View Existing Invoice Option if invoice exists */}
                      {(() => {
                        const existingUrl = getInvoiceUrl(deviceForm.invoiceDetails, selectedDevice?.invoiceDownloadUrl);
                        if (existingUrl) {
                          return (
                            <button
                              type="button"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "8px 14px",
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "#1d4ed8",
                                backgroundColor: "#eff6ff",
                                border: "1px solid #bfdbfe",
                                borderRadius: "6px",
                                cursor: "pointer",
                              }}
                              onClick={() => openInvoiceModal(existingUrl, `Invoice Document (${deviceForm.macId || "Current"})`)}
                              title="View currently uploaded invoice in modal popup"
                            >
                              <MdVisibility size={16} /> View Current Invoice
                            </button>
                          );
                        }
                        return null;
                      })()}

                      {/* Upload New Invoice Button */}
                      <button
                        type="button"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "8px 14px",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: invoiceFile ? "#15803d" : "#374151",
                          backgroundColor: invoiceFile ? "#f0fdf4" : "#ffffff",
                          border: invoiceFile ? "1px solid #86efac" : "1px solid #d1d5db",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                        onClick={() => invoiceFileInputRef.current?.click()}
                      >
                        <MdCloudUpload size={16} />
                        {deviceForm.invoiceDetails ? "Upload New Invoice" : "Upload Invoice Document"}
                      </button>

                      {/* File Selection Status Badge */}
                      {invoiceFile && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "6px 12px", borderRadius: "6px", fontSize: "12px", color: "#166534" }}>
                          <MdCheckCircle size={15} style={{ color: "#16a34a" }} />
                          <button
                            type="button"
                            style={{ background: "none", border: "none", padding: 0, fontWeight: 600, color: "#15803d", cursor: "pointer", textDecoration: "underline" }}
                            title="Preview selected file in modal popup"
                            onClick={() => openInvoiceModal(URL.createObjectURL(invoiceFile), `Selected File (${invoiceFile.name})`)}
                          >
                            {invoiceFile.name}
                          </button>
                          <span style={{ color: "#6b7280" }}>({(invoiceFile.size / 1024).toFixed(1)} KB) - Ready to save</span>
                          <button
                            type="button"
                            title="Remove file and keep current invoice"
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", padding: "0 2px", display: "inline-flex", alignItems: "center" }}
                            onClick={() => {
                              setInvoiceFile(null);
                              if (invoiceFileInputRef.current) invoiceFileInputRef.current.value = "";
                            }}
                          >
                            <MdClose size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    <p style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                      Accepted formats: PDF, PNG, JPG, JPEG (Max: 5 MB).
                      {deviceForm.invoiceDetails && !invoiceFile && " Current invoice will be retained if no new file is selected."}
                    </p>
                  </div>
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="user-modal-footer" style={{ borderTop: "1px solid #e2e8f0", padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button
                  type="button"
                  className="modal-btn-cancel"
                  onClick={() => {
                    setshowForm(false);
                    setMode("");
                  }}
                >
                  Cancel
                </button>
              </div>

              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                <button
                  type="button"
                  className="modal-btn-submit"
                  disabled={isSubmittingDevice}
                  style={{
                    background: "#16a34a",
                    borderColor: "#16a34a",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "10px 26px",
                    fontWeight: 700,
                    fontSize: "14px",
                  }}
                  onClick={handleEditDeviceSubmit}
                >
                  <MdCheckCircle size={18} />
                  {isSubmittingDevice ? "Submitting..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Upload Excel */}
      {canUploadDeviceExcel && showForm && Mode === "multi" && (
        <div
          className="user-modal-overlay"
          onClick={() => {
            setMode("");
            setshowForm(false);
          }}
        >
          <div
            className="user-modal-card"
            style={{ maxWidth: "580px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-modal-header">
              <div className="modal-title-wrap">
                <MdCloudUpload className="modal-header-icon" />
                <h3>Upload Excel Sheet</h3>
              </div>
              <button
                type="button"
                className="user-modal-close-btn"
                onClick={() => {
                  setMode("");
                  setshowForm(false);
                }}
                title="Close"
              >
                <MdClose />
              </button>
            </div>

            <div className="user-modal-body">
              <p style={{ margin: "0 0 16px 0", color: "#6b7280", fontSize: "14px" }}>
                Upload an Excel file (.xlsx, .xls) containing multiple device records.
              </p>

              <div className="user-form-group">
                <label>
                  Devices Excel File <span style={{ color: "#e60000" }}>*</span>
                </label>
                <div className="excel-upload-dropzone">
                  <input
                    type="file"
                    accept=".xlsx, .xls"
                    ref={FileUseRef}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!isValidExtension(file)) {
                        ShowSnackbar("Only .xlsx or .xls files are allowed", "error");
                        e.target.value = "";
                        return;
                      }
                      const maxSize = 1 * 1024 * 1024; // 1 MB
                      if (file.size > maxSize) {
                        ShowSnackbar("File size must not exceed 1 MB", "error");
                        e.target.value = "";
                        setxlFile(null);
                        return;
                      }
                      setxlFile(file);
                    }}
                  />
                  <small style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px" }}>
                    Supports: .xlsx, .xls (Max size: 1 MB)
                  </small>
                </div>
              </div>

              <div style={{ marginTop: "16px", display: "flex", justifyContent: "flex-start" }}>
                <button
                  type="button"
                  className="btn-download-sample"
                  onClick={downloadSampleExcel}
                >
                  <MdDownload size={16} /> Download Sample Template
                </button>
              </div>
            </div>

            <div className="user-modal-footer">
              <button
                type="button"
                className="modal-btn-cancel"
                onClick={() => {
                  resetForm1();
                  setshowForm(false);
                  setMode("");
                }}
              >
                Cancel
              </button>
              <button type="button" className="modal-btn-cancel" onClick={resetForm1}>
                Clear
              </button>
              <button type="button" className="modal-btn-submit" onClick={handleUpload}>
                Upload & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: View Warranty Registration Details */}
      {showForm && Mode === "details" && selectedDevice && (
        <div
          className="user-modal-overlay"
          onClick={() => {
            setMode("");
            setshowForm(false);
          }}
        >
          <div
            className="user-modal-card view-modal-card device-view-modal-card"
            style={{ maxWidth: "1050px", width: "95vw", maxHeight: "92vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-modal-header">
              <div className="modal-title-wrap">
                <MdDevices className="modal-header-icon" />
                <div>
                  <h3 style={{ margin: 0, color: "#ffffff" }}>Warranty Registration Details</h3>
                  <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#ffffff", opacity: 0.95 }}>
                    Complete record data, customer registration parameters, and warranty status
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="user-modal-close-btn"
                onClick={() => {
                  setMode("");
                  setshowForm(false);
                }}
                title="Close"
              >
                <MdClose />
              </button>
            </div>

            <div className="user-modal-body" style={{ overflowY: "auto", maxHeight: "calc(92vh - 140px)" }}>
              {/* Top Device Banner */}
              <div className="view-user-top-profile" style={{ background: "transparent", backgroundColor: "transparent" }}>
                <div className="view-device-avatar">
                  <MdDevices size={32} />
                </div>
                <div
                  className="view-user-top-info"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    textAlign: "left",
                    background: "transparent",
                    backgroundColor: "transparent",
                  }}
                >
                  <div
                    style={{
                      margin: "0 0 4px 0",
                      padding: 0,
                      textAlign: "left",
                      fontSize: "19px",
                      fontWeight: 700,
                      color: "#111827",
                      fontFamily: "monospace",
                      background: "transparent",
                      backgroundColor: "transparent",
                      border: "none",
                      boxShadow: "none",
                    }}
                  >
                    {selectedDevice.macId || "Unknown Device"}
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "13px", color: "#4b5563" }}>
                      Model: <strong style={{ color: "#111827" }}>{selectedDevice.modelname?.toUpperCase() || "N/A"}</strong>
                    </span>
                    <span style={{ color: "#d1d5db" }}>•</span>
                    <span style={{ fontSize: "13px", color: "#4b5563" }}>
                      Firmware: <strong style={{ color: "#111827" }}>{selectedDevice.firmwareversion || "—"}</strong>
                    </span>
                    <span style={{ color: "#d1d5db" }}>•</span>
                    <span className={`warranty-pill ${getWarrantyStatus(selectedDevice)}`}>
                      {getWarrantyStatus(selectedDevice) === "approved"
                        ? "Active Warranty"
                        : getWarrantyStatus(selectedDevice) === "expired"
                        ? "Expired Warranty"
                        : "Pending Warranty"}
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION: Device Warranty Registration Details */}
              <div className="view-details-section">
                <div className="view-details-section-header">
                  <div className="view-details-section-title">
                    <MdDevices style={{ color: "#2563eb", fontSize: "18px" }} />
                    <span>Device Warranty Registration Details</span>
                  </div>
                </div>

                <div className="view-user-grid">
                  <div className="view-detail-item">
                    <span className="view-detail-label">Device MAC ID</span>
                    <span className="view-detail-value" style={{ fontFamily: "monospace", fontWeight: 700, color: "#111827" }}>
                      {selectedDevice.macId || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Model Name</span>
                    <span className="view-detail-value" style={{ fontWeight: 600 }}>
                      {selectedDevice.modelname?.toUpperCase() || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Firmware Version</span>
                    <span className="view-detail-value">
                      {selectedDevice.firmwareversion || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Helmet Brand</span>
                    <span className="view-detail-value">
                      {selectedDevice.helmetBrand?.toUpperCase() || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Device COD</span>
                    <span className="view-detail-value" style={{ fontFamily: "monospace" }}>
                      {selectedDevice.device_cod || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Registered Customer</span>
                    <span className="view-detail-value" style={{ fontWeight: 600 }}>
                      {selectedDevice.username || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Customer Email ID</span>
                    <span className="view-detail-value">
                      {selectedDevice.emailId || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Mobile Number</span>
                    <span className="view-detail-value">
                      {selectedDevice.mobilenumber || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Operating System</span>
                    <span className="view-detail-value">
                      {selectedDevice.OS || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Date of Purchase</span>
                    <span className="view-detail-value">
                      {formatDisplayDate(selectedDevice.dateofPurchase)}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Place of Purchase</span>
                    <span className="view-detail-value">
                      {selectedDevice.placeofPurchase || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Dealer Name</span>
                    <span className="view-detail-value">
                      {selectedDevice.dealerName || "—"}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">City</span>
                    <span className="view-detail-value">{selectedDevice.city || "—"}</span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">State</span>
                    <span className="view-detail-value">{selectedDevice.state || "—"}</span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Country</span>
                    <span className="view-detail-value">{selectedDevice.country || "—"}</span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Postal / Zipcode</span>
                    <span className="view-detail-value">{selectedDevice.zipcode || "—"}</span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Warranty Status</span>
                    <span className="view-detail-value">
                      {(() => {
                        const wStatus = getWarrantyStatus(selectedDevice);
                        return (
                          <span className={`warranty-pill ${wStatus}`}>
                            {wStatus === "approved" ? "Active" : wStatus === "expired" ? "Expired" : "Pending"}
                          </span>
                        );
                      })()}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Invoice Details</span>
                    <span className="view-detail-value">
                      {(() => {
                        const invUrl = getInvoiceUrl(selectedDevice.invoiceDetails, selectedDevice.invoiceDownloadUrl);
                        if (invUrl) {
                          return (
                            <button
                              type="button"
                              className="action-btn view"
                              style={{ width: "fit-content", padding: "4px 10px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                              onClick={() => openInvoiceModal(invUrl, `Invoice Document (${selectedDevice.macId || "Device"})`)}
                              title="View invoice in modal popup"
                            >
                              <MdVisibility size={14} /> View Invoice
                            </button>
                          );
                        }
                        return selectedDevice.invoiceDetails || "No Invoice Attached";
                      })()}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Registration Date</span>
                    <span className="view-detail-value">
                      {formatDisplayDateTime(selectedDevice.createdAt)}
                    </span>
                  </div>

                  <div className="view-detail-item">
                    <span className="view-detail-label">Last Updated</span>
                    <span className="view-detail-value">
                      {formatDisplayDateTime(selectedDevice.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="user-modal-footer" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  type="button"
                  className="modal-btn-cancel"
                  onClick={() => {
                    setMode("");
                    setshowForm(false);
                  }}
                >
                  Close
                </button>
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                {canEditDevice && (
                  <button
                    type="button"
                    className="modal-btn-submit"
                    style={{ background: "#2563eb", borderColor: "#2563eb", display: "inline-flex", alignItems: "center", gap: "6px" }}
                    onClick={() => editdevice(selectedDevice)}
                  >
                    <MdEdit size={16} /> Edit Warranty Registration
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: View Current Invoice Popup */}
      {invoiceModalUrl && (
        <div
          className="user-modal-overlay invoice-modal-overlay"
          onClick={() => setInvoiceModalUrl(null)}
        >
          <div
            className="user-modal-card invoice-modal-card"
            style={{
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 60px rgba(0, 0, 0, 0.55)",
              borderRadius: "12px",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="user-modal-header"
              style={{
                background: "#08284d",
                padding: "16px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="modal-title-wrap" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <MdVisibility className="modal-header-icon" style={{ fontSize: "22px", color: "#60a5fa" }} />
                <div>
                  <h3 style={{ margin: 0, color: "#ffffff", fontSize: "16px", fontWeight: 700 }}>
                    {invoiceModalTitle}
                  </h3>
                  <span style={{ fontSize: "12px", color: "#cbd5e1" }}>
                    Warranty Invoice Document Preview
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <a
                  href={invoiceModalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#ffffff",
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    border: "1px solid rgba(255, 255, 255, 0.25)",
                    padding: "6px 14px",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: 600,
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  title="Open document in a new browser tab or download"
                >
                  <MdDownload size={14} /> Open in New Tab
                </a>
                <button
                  type="button"
                  className="user-modal-close-btn"
                  onClick={() => setInvoiceModalUrl(null)}
                  title="Close Invoice Preview"
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    border: "none",
                    color: "#ffffff",
                    borderRadius: "6px",
                    padding: "6px",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MdClose size={18} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div
              className="user-modal-body"
              style={{
                flex: 1,
                padding: "16px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "450px",
                background: "#f1f5f9",
              }}
            >
              {isPdfFile(invoiceModalUrl) ? (
                <iframe
                  src={invoiceModalUrl}
                  title="Invoice PDF Document"
                  style={{
                    width: "100%",
                    height: "72vh",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "10px",
                  }}
                >
                  <img
                    src={invoiceModalUrl}
                    alt="Invoice Document Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "72vh",
                      objectFit: "contain",
                      borderRadius: "8px",
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
                      border: "1px solid #cbd5e1",
                      backgroundColor: "#ffffff",
                    }}
                  />
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="user-modal-footer"
              style={{
                borderTop: "1px solid #e2e8f0",
                padding: "12px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#ffffff",
              }}
            >
              <span style={{ fontSize: "12px", color: "#64748b" }}>
                Document: <strong>{invoiceModalUrl.split("/").pop()?.split("?")[0] || "invoice"}</strong>
              </span>
              <button
                type="button"
                className="modal-btn-cancel"
                onClick={() => setInvoiceModalUrl(null)}
                style={{ padding: "8px 18px", fontSize: "13px" }}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar Notifications */}
      <ShowSnacker
        open={snack.open}
        message={snack.message}
        type={snack.type}
        className="devices-snackbar"
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </div>
  );
}

export default DashBoardDevices;

